import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CategoryContext = createContext(null);
const EMPTY = { expense: [], income: [], asset: [] };

export function CategoryProvider({ children }) {
  const { user } = useAuth();
  const [cats, setCats] = useState(EMPTY);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCats({
        expense: data.expense || [],
        income: data.income || [],
        asset: data.asset || [],
      });
    } catch {
      /* not authenticated yet — ignore */
    }
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") reload();
  }, [user, reload]);

  const addCategory = useCallback(async (type, name) => {
    const { data } = await api.post("/categories", { type, name });
    setCats((c) => ({ ...c, [type]: data.categories }));
    return data.categories;
  }, []);

  const removeCategory = useCallback(async (type, name) => {
    const { data } = await api.delete("/categories", { params: { type, name } });
    setCats((c) => ({ ...c, [type]: data.categories }));
    return data.categories;
  }, []);

  const value = useMemo(
    () => ({ cats, reload, addCategory, removeCategory }),
    [cats, reload, addCategory, removeCategory]
  );

  return <CategoryContext.Provider value={value}>{children}</CategoryContext.Provider>;
}

export const useCategories = () =>
  useContext(CategoryContext) || {
    cats: EMPTY,
    reload: () => {},
    addCategory: async () => {},
    removeCategory: async () => {},
  };
